import { useCallback, useEffect, useRef, useState } from 'react'
import { useDebounce } from '@src/hooks/useDebounce'

export const useSlideWidthCalculator = (isMobile: boolean) => {
  const [slideWidth, setSlideWidth] = useState<number>(140)
  const [slideCount, setSlideCount] = useState<number>(8)
  const [containerWidth, setContainerWidth] = useState<number>(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const debouncedContainerWidth = useDebounce(containerWidth, 300)

  // Calculate slide width based on container size and device type
  const calculateOptimalSlideWidth = useCallback(
    (width: number) => {
      if (width === 0) return

      const gap = 8 // spaceBetween value - always 8px
      const minWidth = isMobile ? 100 : 140
      const maxWidth = isMobile ? 130 : 155

      // Calculate how many slides can fit with the minimum width
      let slidesCount = Math.floor((width + gap) / (minWidth + gap))

      // Calculate the exact width that ensures perfect 8px gaps
      let exactWidth = (width - gap * (slidesCount - 1)) / slidesCount

      // If the exact width is too small, reduce number of slides by 1
      if (exactWidth < minWidth && slidesCount > 1) {
        slidesCount--
        exactWidth = (width - gap * (slidesCount - 1)) / slidesCount
      }

      // If the exact width is too large, increase number of slides by 1
      if (exactWidth > maxWidth) {
        slidesCount++
        exactWidth = (width - gap * (slidesCount - 1)) / slidesCount
      }

      
      setSlideWidth(exactWidth)
      setSlideCount(slidesCount)
    },
    [isMobile]
  )

  // Set up container width observer
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth)
      }
    })

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
      // Initial calculation
      setContainerWidth(containerRef.current.clientWidth)
    }

    return () => resizeObserver.disconnect()
  }, [])

  // Update slide width when container width changes
  useEffect(() => {
    calculateOptimalSlideWidth(debouncedContainerWidth)
  }, [debouncedContainerWidth, calculateOptimalSlideWidth])

  return { slideWidth, slideCount, containerRef }
} 